/**
 * DomainProfileService resolves the runtime domain profile (local/drop/prod) from
 * the current browser hostname and applies the matching page title and favicon.
 * The active profile accent class is exposed as a signal for consumers (e.g. sidebar accent).
 */

import { computed, Service } from '@angular/core';

export type DomainProfileName = 'local' | 'drop' | 'prod';

export interface DomainProfile {
    /** Identifier of the resolved profile. */
    readonly name: DomainProfileName;
    /** Page title to set on the document. */
    readonly title: string;
    /** Favicon href to set on the document's icon link element. */
    readonly faviconHref: string;
    /** MIME type for the favicon link element. */
    readonly faviconType: string;
    /** Tailwind background classes for the sidebar logo box. */
    readonly accentClass: string;
}

@Service()
export class DomainProfileService {
    private readonly _profiles: Readonly<Record<DomainProfileName, DomainProfile>> = {
        local: {
            name: 'local',
            title: 'WOLF: Local',
            faviconHref: 'assets/favicon-local.png',
            faviconType: 'image/png',
            accentClass: 'bg-orange-500 dark:bg-orange-400',
        },
        drop: {
            name: 'drop',
            title: 'WOLF: Drop',
            faviconHref: 'assets/favicon-drop.png',
            faviconType: 'image/png',
            accentClass: 'bg-blue-400',
        },
        prod: {
            name: 'prod',
            title: 'Advania: Wolf',
            faviconHref: 'assets/favicon.png',
            faviconType: 'image/png',
            accentClass: 'bg-primary-500 dark:bg-primary-400',
        },
    };

    private readonly _profile = computed(() => {
        const normalized = window.location.hostname.toLowerCase();

        switch (normalized) {
            case 'localhost':
                return this._profiles.local;
            case 'wolf-drop.advania.no':
                return this._profiles.drop;
            default:
                return this._profiles.prod;
        }
    });

    readonly accentClass = computed(() => this._profile().accentClass);

    constructor() {
        const profile = this._profile();

        document.title = profile.title;

        const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
        if (link) {
            link.href = profile.faviconHref;
            link.type = profile.faviconType;
        }
    }
}
